import { useEffect, useRef, useState, useCallback } from 'react';
import { ChatClient } from '@azure/communication-chat';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';

/**
 * ChatPanel — shared real-time chat UI for both carrier and scheduler sides.
 *
 * Given an ACS connection bundle (endpoint, token, threadId, our own ACS user
 * id), it connects directly to ACS for real-time messaging: loads history,
 * subscribes to new messages + typing indicators, and sends messages.
 *
 * The parent obtains the bundle from our backend (carrier: POST chat/threads/open,
 * scheduler: POST chat/threads/{id}/token) and passes it in. This component
 * knows nothing about how threads are authorized — that already happened.
 */

export interface ChatConnection {
    endpoint: string;
    token: string;
    acsThreadId: string;
    acsUserId: string;
    displayName: string;
}

interface ChatMessage {
    id: string;
    content: string;
    senderId: string;
    senderDisplayName: string;
    createdOn: Date;
    isMine: boolean;
}

export default function ChatPanel({ connection, onMessageSent }: {
    connection: ChatConnection;
    onMessageSent?: () => void;
}) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());

    const chatClientRef = useRef<ChatClient | null>(null);
    const threadClientRef = useRef<ReturnType<ChatClient['getChatThreadClient']> | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const lastTypingSent = useRef<number>(0);
    const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
    }, []);

    // Connect + subscribe
    useEffect(() => {
        let cancelled = false;
        const credential = new AzureCommunicationTokenCredential(connection.token);
        const chatClient = new ChatClient(connection.endpoint, credential);
        chatClientRef.current = chatClient;
        const threadClient = chatClient.getChatThreadClient(connection.acsThreadId);
        threadClientRef.current = threadClient;

        async function init() {
            try {
                // Load history (most recent first from ACS; we reverse to chrono).
                const loaded: ChatMessage[] = [];
                for await (const m of threadClient.listMessages({ maxPageSize: 50 })) {
                    if (m.type !== 'text' || !m.content?.message) continue;
                    loaded.push({
                        id: m.id,
                        content: m.content.message,
                        senderId: (m.sender as { communicationUserId?: string })?.communicationUserId ?? '',
                        senderDisplayName: m.senderDisplayName ?? 'Unknown',
                        createdOn: m.createdOn,
                        isMine: (m.sender as { communicationUserId?: string })?.communicationUserId === connection.acsUserId,
                    });
                }
                if (cancelled) return;
                loaded.sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());
                setMessages(loaded);
                scrollToBottom();

                await chatClient.startRealtimeNotifications();
                if (cancelled) return;

                chatClient.on('chatMessageReceived', (e) => {
                    if (e.threadId !== connection.acsThreadId) return;
                    const senderId = (e.sender as { communicationUserId?: string })?.communicationUserId ?? '';
                    setMessages(prev => {
                        if (prev.some(m => m.id === e.id)) return prev;
                        return [...prev, {
                            id: e.id,
                            content: e.message,
                            senderId,
                            senderDisplayName: e.senderDisplayName ?? 'Unknown',
                            createdOn: new Date(e.createdOn),
                            isMine: senderId === connection.acsUserId,
                        }];
                    });
                    // Clear that sender's typing indicator on a real message.
                    setTypingUsers(prev => { const n = new Set(prev); n.delete(senderId); return n; });
                    scrollToBottom();
                });

                chatClient.on('typingIndicatorReceived', (e) => {
                    if (e.threadId !== connection.acsThreadId) return;
                    const senderId = (e.sender as { communicationUserId?: string })?.communicationUserId ?? '';
                    if (senderId === connection.acsUserId) return;
                    const name = e.senderDisplayName ?? 'Someone';
                    setTypingUsers(prev => new Set(prev).add(name));
                    const existing = typingTimers.current.get(name);
                    if (existing) clearTimeout(existing);
                    const t = setTimeout(() => {
                        setTypingUsers(prev => { const n = new Set(prev); n.delete(name); return n; });
                    }, 4000);
                    typingTimers.current.set(name, t);
                });

                setConnected(true);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Could not connect to chat.');
            }
        }
        init();

        return () => {
            cancelled = true;
            for (const t of typingTimers.current.values()) clearTimeout(t);
            typingTimers.current.clear();
            chatClient.stopRealtimeNotifications().catch(() => {});
        };
    }, [connection, scrollToBottom]);

    const handleSend = async () => {
        const text = draft.trim();
        if (!text || !threadClientRef.current) return;
        setDraft('');
        try {
            await threadClientRef.current.sendMessage(
                { content: text },
                { senderDisplayName: connection.displayName },
            );
            onMessageSent?.();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Message failed to send.');
            setDraft(text);
        }
    };

    const handleTyping = () => {
        const now = Date.now();
        if (now - lastTypingSent.current > 3000 && threadClientRef.current) {
            lastTypingSent.current = now;
            threadClientRef.current.sendTypingNotification().catch(() => {});
        }
    };

    const typingList = Array.from(typingUsers);

    return (
        <div className="flex flex-col h-full min-h-0">
            {error && (
                <div className="text-xs rounded-md bg-red-50 text-red-700 px-3 py-2 mb-2">{error}</div>
            )}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto space-y-2 px-1 py-2">
                {messages.length === 0 && connected && (
                    <div className="text-center text-xs text-gray-400 py-8">No messages yet. Say hello.</div>
                )}
                {!connected && !error && (
                    <div className="text-center text-xs text-gray-400 py-8 animate-pulse">Connecting…</div>
                )}
                {messages.map(m => (
                    <div key={m.id} className={`flex ${m.isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                            m.isMine ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
                        }`}>
                            {!m.isMine && (
                                <div className="text-[10px] font-medium opacity-70 mb-0.5">{m.senderDisplayName}</div>
                            )}
                            <div className="whitespace-pre-wrap break-words">{m.content}</div>
                            <div className={`text-[10px] mt-0.5 ${m.isMine ? 'text-blue-100' : 'text-gray-400'}`}>
                                {m.createdOn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </div>
                        </div>
                    </div>
                ))}
                {typingList.length > 0 && (
                    <div className="text-xs text-gray-400 italic px-1">
                        {typingList.join(', ')} {typingList.length === 1 ? 'is' : 'are'} typing…
                    </div>
                )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-200">
                <textarea
                    value={draft}
                    onChange={e => { setDraft(e.target.value); handleTyping(); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type a message…"
                    rows={1}
                    disabled={!connected}
                    className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50"
                />
                <button
                    onClick={handleSend}
                    disabled={!connected || !draft.trim()}
                    className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40"
                >
                    Send
                </button>
            </div>
        </div>
    );
}
